import { useEffect, useMemo, useState } from "react";
import { BASE_TICK_MS, SPEED_OPTIONS } from "./constants";
import { runSimulationStep } from "./engine";
import { createInitialSimulationState } from "./seed";
import { formatStepTime } from "./time";
import type { SimulationState, VehicleAgent } from "./types";

const nextUpcomingTrip = (vehicle: VehicleAgent, currentStep: number) =>
  vehicle.trips.find((trip) => !trip.completed && trip.departureStep >= currentStep) || null;

export const useSimulation = () => {
  const [simulation, setSimulation] = useState<SimulationState>(() => createInitialSimulationState());
  const [speedMultiplier, setSpeedMultiplier] = useState<number>(1);

  const startSimulation = () => {
    if (simulation.completed) {
      return;
    }
    setSimulation((prev) => ({ ...prev, running: true }));
  };

  const resetSimulation = () => {
    setSimulation(createInitialSimulationState());
    setSpeedMultiplier(1);
  };

  useEffect(() => {
    if (!simulation.running || simulation.completed) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setSimulation((prev) => runSimulationStep(prev));
    }, BASE_TICK_MS / speedMultiplier);
    return () => window.clearInterval(timer);
  }, [simulation.completed, simulation.running, speedMultiplier]);

  const stationDemandData = useMemo(() => {
    return Array.from({ length: simulation.totalSteps }, (_, step) => ({
      step: step + 1,
      time: formatStepTime(step),
      stationA: simulation.stations.stationA.demandProfileKw[step] || 0,
      stationB: simulation.stations.stationB.demandProfileKw[step] || 0,
    }));
  }, [simulation.stations.stationA.demandProfileKw, simulation.stations.stationB.demandProfileKw, simulation.totalSteps]);

  const vehicleRows = useMemo(
    () =>
      simulation.vehicles.map((vehicle) => {
        const nextTrip = nextUpcomingTrip(vehicle, simulation.step);
        return {
          id: vehicle.id,
          socPct: (vehicle.socKWh / vehicle.batteryCapacityKWh) * 100,
          state: vehicle.currentState,
          station: vehicle.position.stationId || vehicle.homeStationId,
          nextTrip: nextTrip
            ? `${formatStepTime(nextTrip.departureStep)}-${formatStepTime(nextTrip.returnStep)} (${nextTrip.distanceKm} km)`
            : "No remaining trips",
        };
      }),
    [simulation.step, simulation.vehicles],
  );

  const selectedSpeed = SPEED_OPTIONS.find((option) => option.value === speedMultiplier)?.value || 1;

  return {
    simulation,
    speedMultiplier,
    selectedSpeed,
    stationDemandData,
    vehicleRows,
    currentTimeLabel: formatStepTime(Math.min(simulation.step, simulation.totalSteps - 1)),
    setSpeedMultiplier,
    startSimulation,
    resetSimulation,
  };
};
